import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Lightning, Plus, Trash, ArrowClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { probeLan, getLanBase } from '../../lib/lanStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LAN 直結ダウンロード設定カード for /settings/storage.
 *
 * Lets the admin manage which LAN base URLs the browser should probe on
 * login. Each entry MUST be a private RFC1918/RFC6598 address (validated
 * server-side; UI shows the reason on rejection).
 *
 * The currently-active winner (this browser's cached LAN base) is
 * highlighted with a green "アクティブ" pill so admins can verify that
 * their office LAN is reaching the NAS from wherever they are testing.
 */
export function LanProbesCard() {
  const [urls, setUrls] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);
  const [activeBase, setActiveBase] = useState(getLanBase());

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/storage/lan-probes`);
      setUrls(r.data?.urls || []);
    } catch (_e) {
      toast.error('LAN プローブ URL の取得に失敗しました');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => setActiveBase(getLanBase());
    window.addEventListener('lan-base-changed', handler);
    return () => window.removeEventListener('lan-base-changed', handler);
  }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/storage/lan-probes`, { urls: next });
      setUrls(r.data?.urls || []);
      toast.success('保存しました');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'LAN プローブ URL の保存に失敗しました';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const addUrl = async () => {
    const u = (newUrl || '').trim();
    if (!u) return;
    if (urls.includes(u)) { toast.info('すでに登録されています'); return; }
    await persist([...urls, u]);
    setNewUrl('');
  };

  const removeUrl = async (u) => {
    if (!window.confirm(`「${u}」を LAN プローブから削除しますか？`)) return;
    await persist(urls.filter((x) => x !== u));
  };

  const reprobe = async () => {
    setProbing(true);
    try {
      const winner = await probeLan(urls);
      setActiveBase(winner);
      if (winner) toast.success(`LAN 直結 ON: ${winner}`);
      else toast.info('LAN 直結できるサーバーが見つかりませんでした（WAN経由でダウンロードします）');
    } finally {
      setProbing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="storage-lan-probes-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-emerald-700 shrink-0">
          <Lightning size={20} weight="duotone" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-zinc-900">LAN 直結ダウンロード</h2>
          <p className="text-[12px] text-zinc-500">
            社内 LAN・VPN からアクセス時、NAS へ直接ダウンロードして高速化します（自動判定・失敗時は自動 WAN フォールバック）。
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reprobe} disabled={probing} data-testid="storage-lan-probe-btn" className="shrink-0">
          <ArrowClockwise size={14} className={`mr-1 ${probing ? 'animate-spin' : ''}`} />
          {probing ? '確認中…' : '再判定'}
        </Button>
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-800 flex items-center gap-2" data-testid="storage-lan-active-status">
          {activeBase ? (
            <>
              <CheckCircle size={16} weight="duotone" className="text-emerald-600 shrink-0" />
              <span>この端末では <b className="font-mono">{activeBase}</b> 経由で直接ダウンロードしています。</span>
            </>
          ) : (
            <>
              <XCircle size={16} weight="duotone" className="text-zinc-400 shrink-0" />
              <span className="text-zinc-600">この端末は LAN 直結できません。WAN (Cloudflare) 経由でダウンロードしています。</span>
            </>
          )}
        </div>

        <ul className="rounded-md border border-zinc-200 divide-y divide-zinc-200" data-testid="storage-lan-list">
          {urls.length === 0 && (
            <li className="px-3 py-3 text-[12px] text-zinc-500">
              プローブ URL が登録されていません。下のフォームから NAS の LAN IP と ポート（例: <code className="bg-zinc-100 px-1 rounded">http://192.168.0.205:8788</code>）を追加してください。
            </li>
          )}
          {urls.map((u) => (
            <li key={u} className="px-3 py-2 flex items-center gap-2" data-testid={`storage-lan-row-${u}`}>
              <span className={`inline-block w-2 h-2 rounded-full ${u.replace(/\/$/, '') === activeBase ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
              <code className="flex-1 min-w-0 font-mono text-[12px] text-zinc-800 truncate">{u}</code>
              {u.replace(/\/$/, '') === activeBase && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">アクティブ</span>
              )}
              <Button variant="ghost" size="sm" onClick={() => removeUrl(u)} disabled={saving} data-testid={`storage-lan-remove-${u}`} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-7 px-2">
                <Trash size={14} />
              </Button>
            </li>
          ))}
        </ul>

        <div className="flex gap-2">
          <Input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addUrl(); }}
            placeholder="http://192.168.0.205:8788"
            data-testid="storage-lan-add-input"
            className="flex-1 font-mono text-[12px]"
          />
          <Button onClick={addUrl} disabled={saving || !newUrl.trim()} data-testid="storage-lan-add-btn" className="shrink-0">
            <Plus size={14} className="mr-1" />
            追加
          </Button>
        </div>

        <p className="text-[11px] text-zinc-500">
          ※ HTTPS の URL（例: <code>https://nas.sensbey.link</code>）は任意のホストで登録できます — LAN 上では DNS リライトで NAS のプライベート IP に解決されるため、HTTPS ページからも <b>ミックスコンテンツにブロックされずに</b> プローブできます。<br />
          HTTP の URL はプライベート IP のみ (<code>10.x.x.x</code> / <code>192.168.x.x</code> / <code>172.16-31.x.x</code> / <code>100.64-127.x.x</code>)。パブリックホストは拒否されます。
        </p>

        <p className="text-[11px] text-zinc-500 mt-1">
          ⚠️ 本アプリは HTTPS で配信されているため、ブラウザは HTTP の LAN プローブを<b>ミックスコンテンツ</b>としてブロックします。恒久的に LAN 直結を有効化するには <b>Caddy + Let's Encrypt</b> を NAS 側にセットアップして <code>https://nas.your-domain.link</code> をプローブに追加してください（DNS リライトで LAN では NAS IP、WAN では Cloudflare に解決されます）。
        </p>
      </div>
    </div>
  );
}

export default LanProbesCard;
